/**
 * GlobalView — EW / Signal Layer Renderer (§4.4)
 *
 * Renders GPS interference heatmap from GPSJam.org data.
 * Uses H3 hexagonal cells rendered as glowing vertical beams
 * (Cesium WallGraphics) projecting into the sky.
 *
 * Features:
 *   - H3 hex → wall beam conversion via h3-js
 *   - Color coding: yellow (moderate), red (significant)
 *   - Glowing translucent walls with graduated height by severity
 *   - Faint ground polygon as base glow
 *   - Visibility controlled by globeStore.ewLayerVisible
 *   - Data fetched on toggle-on, cached per day
 */

import * as Cesium from 'cesium';
import { cellToBoundary, isValidCell } from 'h3-js';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore, selectJammingCellCount } from '../../stores/entityStore';
import {
  fetchJammingData,
  fetchLatestDate,
  jammingCellsToEntities,
  type JammingCell,
} from '../gpsjamClient';

// ─── Config ──────────────────────────────────────────────────────

/** Wall beam height by severity (meters). Red = taller, more dramatic. */
const WALL_HEIGHT_YELLOW = 25_000;
const WALL_HEIGHT_RED = 60_000;

/**
 * Number of gradient bands per beam.
 * More bands = smoother fade. 20 is a good balance of smoothness vs entity count.
 */
const BAND_COUNT = 20;

/** Base alpha for the brightest (ground-level) band */
const BASE_ALPHA_WALL = 0.30;
const GROUND_ALPHA = 0.15;

/**
 * Generate smooth gradient bands using exponential decay.
 * Alpha follows e^(-k*t) where t is 0..1 (bottom to top).
 * This produces a natural, smooth fade with no visible steps.
 */
function generateGradientBands(): [number, number, number][] {
  const bands: [number, number, number][] = [];
  const decay = 4.0; // steepness of the falloff

  for (let i = 0; i < BAND_COUNT; i++) {
    const fracStart = i / BAND_COUNT;
    const fracEnd = (i + 1) / BAND_COUNT;
    // Midpoint alpha for this band — exponential decay
    const midpoint = (fracStart + fracEnd) / 2;
    const alphaMult = Math.exp(-decay * midpoint);
    bands.push([fracStart, fracEnd, alphaMult]);
  }

  return bands;
}

const GRADIENT_BANDS = generateGradientBands();

/** Beam colors — warm orange/red palette for "jamming" look */
const BASE_COLOR_YELLOW = Cesium.Color.fromCssColorString('#ffaa00');
const BASE_COLOR_RED = Cesium.Color.fromCssColorString('#ff2222');

const GROUND_COLOR_YELLOW = Cesium.Color.fromCssColorString('#ffaa00').withAlpha(GROUND_ALPHA);
const GROUND_COLOR_RED = Cesium.Color.fromCssColorString('#ff2222').withAlpha(GROUND_ALPHA);

// ─── Module State ────────────────────────────────────────────────

interface EWLayerState {
  viewer: Cesium.Viewer;
  dataSource: Cesium.CustomDataSource;
  unsubscribeVisibility: (() => void) | null;
  loadedDate: string | null;
  loading: boolean;
}

let state: EWLayerState | null = null;

// ─── Helpers ─────────────────────────────────────────────────────

function getBaseColor(level: string): Cesium.Color {
  return level === 'red' ? BASE_COLOR_RED : BASE_COLOR_YELLOW;
}

function getGroundColor(level: string): Cesium.Color {
  return level === 'red' ? GROUND_COLOR_RED : GROUND_COLOR_YELLOW;
}

function getWallHeight(level: string): number {
  return level === 'red' ? WALL_HEIGHT_RED : WALL_HEIGHT_YELLOW;
}

/**
 * Convert an H3 hex index to a closed ring of Cartesian3 positions.
 * Returns null if the H3 index is invalid.
 */
function h3ToBoundaryPositions(h3Index: string): Cesium.Cartesian3[] | null {
  if (!isValidCell(h3Index)) return null;

  try {
    // cellToBoundary returns [[lat, lng], ...] — note: lat/lng not lng/lat!
    const boundary = cellToBoundary(h3Index);
    if (boundary.length < 3) return null;

    const positions = boundary.map(([lat, lng]: number[]) =>
      Cesium.Cartesian3.fromDegrees(lng, lat),
    );

    // Close the ring for WallGraphics
    positions.push(positions[0]);
    return positions;
  } catch {
    return null;
  }
}

// ─── Rendering ───────────────────────────────────────────────────

/**
 * Render jamming cells as glowing hexagonal beam pillars with gradient fade.
 * Each cell gets:
 *   - Multiple stacked WallGraphics bands with decreasing alpha (bright→transparent)
 *   - A faint ground polygon as the "base glow"
 * Only renders interference cells (yellow + red).
 */
function renderCells(cells: JammingCell[]): void {
  if (!state) return;

  const { dataSource } = state;
  dataSource.entities.removeAll();

  let renderedCount = 0;

  for (const cell of cells) {
    // Skip green cells — only show interference
    if (cell.level === 'green') continue;

    const positions = h3ToBoundaryPositions(cell.h3Index);
    if (!positions) continue;

    const baseColor = getBaseColor(cell.level);
    const groundColor = getGroundColor(cell.level);
    const totalHeight = getWallHeight(cell.level);

    // ── Gradient wall bands (bright at ground → transparent at top) ──
    for (let i = 0; i < GRADIENT_BANDS.length; i++) {
      const [fracStart, fracEnd, alphaMult] = GRADIENT_BANDS[i];
      const bandAlpha = BASE_ALPHA_WALL * alphaMult;

      const minH = new Array(positions.length).fill(totalHeight * fracStart);
      const maxH = new Array(positions.length).fill(totalHeight * fracEnd);

      dataSource.entities.add({
        id: `ew-beam-${cell.h3Index}-b${i}`,
        wall: {
          positions: new Cesium.ConstantProperty(positions),
          minimumHeights: new Cesium.ConstantProperty(minH),
          maximumHeights: new Cesium.ConstantProperty(maxH),
          material: new Cesium.ColorMaterialProperty(baseColor.withAlpha(bandAlpha)),
          outline: false,
        },
        // Only store properties on the first band
        ...(i === 0 ? {
          properties: new Cesium.PropertyBag({
            h3Index: cell.h3Index,
            level: cell.level,
            countGood: cell.countGood,
            countBad: cell.countBad,
            total: cell.total,
            percentage: cell.percentage.toFixed(1),
          }),
        } : {}),
      });
    }

    // ── Ground glow (faint base polygon) ──
    const groundPositions = positions.slice(0, -1);
    dataSource.entities.add({
      id: `ew-base-${cell.h3Index}`,
      polygon: {
        hierarchy: new Cesium.PolygonHierarchy(groundPositions),
        material: new Cesium.ColorMaterialProperty(groundColor),
        outline: false,
        height: new Cesium.ConstantProperty(0),
        classificationType: new Cesium.ConstantProperty(Cesium.ClassificationType.BOTH),
      },
    });

    renderedCount++;
  }

  console.log(`[EWLayer] Rendered ${renderedCount} interference beams (${renderedCount * (GRADIENT_BANDS.length + 1)} entities)`);
}

/**
 * Load and render the latest GPSJam data.
 */
async function loadData(): Promise<void> {
  if (!state || state.loading) return;

  state.loading = true;
  console.log('[EWLayer] Loading GPSJam data...');

  try {
    const date = await fetchLatestDate();
    if (!date) {
      console.warn('[EWLayer] No date available from GPSJam');
      return;
    }

    // Skip if already loaded for this date
    if (state.loadedDate === date) {
      console.log(`[EWLayer] Already loaded for ${date}`);
      return;
    }

    const cells = await fetchJammingData(date);
    if (cells.length === 0) {
      console.warn('[EWLayer] No jamming data received');
      return;
    }

    // Store in entity store for consistent data model
    const entities = jammingCellsToEntities(cells, date);
    useEntityStore.getState().upsertEntities(entities);

    // Render polygons
    renderCells(cells);
    state.loadedDate = date;

    console.log(`[EWLayer] Loaded ${cells.length} cells for ${date}`);
  } catch (err) {
    console.error('[EWLayer] Failed to load data:', err);
  } finally {
    if (state) state.loading = false;
  }
}

// ─── Public API ──────────────────────────────────────────────────

export function initEWLayer(viewer: Cesium.Viewer): void {
  if (state) return;

  const dataSource = new Cesium.CustomDataSource('ew-signal');
  viewer.dataSources.add(dataSource);

  state = {
    viewer,
    dataSource,
    unsubscribeVisibility: null,
    loadedDate: null,
    loading: false,
  };

  // Set initial visibility
  dataSource.show = useGlobeStore.getState().ewLayerVisible;

  // Subscribe to visibility changes
  state.unsubscribeVisibility = useGlobeStore.subscribe((curr, prev) => {
    if (!state) return;

    if (curr.ewLayerVisible !== prev.ewLayerVisible) {
      state.dataSource.show = curr.ewLayerVisible;

      // Lazy-load data when first toggled on
      if (curr.ewLayerVisible && !state.loadedDate) {
        loadData();
      }
    }
  });

  // If already visible at init, load data
  if (useGlobeStore.getState().ewLayerVisible) {
    loadData();
  }

  console.log('[EWLayer] Initialized');
}

export function destroyEWLayer(): void {
  if (!state) return;

  if (state.unsubscribeVisibility) state.unsubscribeVisibility();

  state.viewer.dataSources.remove(state.dataSource, true);

  // Clear jamming entities from store
  useEntityStore.getState().clearByType('jamming_cell');

  state = null;
  console.log('[EWLayer] Destroyed');
}

/**
 * Get the count of currently rendered interference cells.
 */
export function getEWCellCount(): number {
  return selectJammingCellCount(useEntityStore.getState());
}
